import { registerSlider } from '@theme/lib-swiper-manager';
import { debounce, shouldReduceMotion } from '@theme/utilities';
import {
  ensureVideoPlayer,
  getVisibleRealSlideIndexes,
  initVideoPlayers,
  pauseAllVideos,
  pauseInactiveVideos,
  playAutoplayVideos,
  playVideoPlayer,
  syncResponsiveVideoPlayers,
} from '@theme/lib-video-player';

const DEFAULT_TRANSITION_DURATION = 550;
const REDUCED_MOTION_TRANSITION_DURATION = 300;
const INTERACTION_RESUME_DELAY = 1200;

/**
 * @param {number} number
 * @param {number} total
 * @returns {string}
 */
function formatSlideStatus(number, total) {
  const template =
    Theme.translations.testimonials_slide_status ??
    'Slide {{ number }} of {{ total }}';
  return template
    .replace('{{ number }}', String(number))
    .replace('{{ total }}', String(total));
}

/**
 * @param {Element} swiperEl
 * @returns {Element[]}
 */
function getRealSlides(swiperEl) {
  return Array.from(
    swiperEl.querySelectorAll('.swiper-wrapper > .swiper-slide')
  ).filter((slide) => !slide.classList.contains('swiper-slide-duplicate'));
}

/**
 * @param {HTMLElement} sectionEl
 * @param {'columnsMobile' | 'columnsTablet' | 'columnsDesktop'} key
 * @param {number} fallback
 * @returns {number}
 */
function readColumnCount(sectionEl, key, fallback) {
  const value = Number(sectionEl.dataset[key]);
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

/**
 * @param {HTMLElement} sectionEl
 * @param {'gap' | 'gapMobile' | 'gapTablet' | 'gapDesktop'} key
 * @param {number} fallback
 * @returns {number}
 */
function readGap(sectionEl, key, fallback) {
  const value = Number(sectionEl.dataset[key]);
  return Number.isFinite(value) ? value : fallback;
}

/**
 * @param {HTMLElement} sectionEl
 * @param {string} swipeKey
 * @param {string} visibleKey
 * @param {'columnsMobile' | 'columnsTablet' | 'columnsDesktop'} columnsKey
 * @param {number} fallback
 * @returns {number}
 */
function readSlidesPerView(
  sectionEl,
  swipeKey,
  visibleKey,
  columnsKey,
  fallback
) {
  if (sectionEl.dataset[swipeKey] === 'true') {
    const visible = Number(sectionEl.dataset[visibleKey]);
    if (Number.isFinite(visible) && visible > 0) return visible;
  }
  return readColumnCount(sectionEl, columnsKey, fallback);
}

/**
 * @param {HTMLElement} sectionEl
 * @param {SwiperInstance | null} swiper
 */
function updateSwipeHint(sectionEl, swiper) {
  const hintText = sectionEl.querySelector('.hs-hint .hs-hint-text');
  if (!(hintText instanceof HTMLElement) || !swiper) return;

  const defaultHint =
    hintText.dataset.hintDefault ||
    Theme.translations.featured_collection_swipe_hint ||
    '';
  const endHint =
    hintText.dataset.hintEnd ||
    Theme.translations.featured_collection_all_scrolled ||
    '';

  if (swiper.params?.loop) {
    hintText.classList.remove('is-muted');
    hintText.textContent = defaultHint;
    return;
  }

  const atEnd = swiper.isEnd === true;
  hintText.classList.toggle('is-muted', atEnd);
  hintText.textContent = atEnd ? endHint : defaultHint;
}

/**
 * @param {HTMLElement} sectionEl
 * @returns {boolean}
 */
function isFeaturedVideoPlaying(sectionEl) {
  return sectionEl.querySelector('.testimonial-card--playing') !== null;
}

/**
 * @param {HTMLElement} sectionEl
 */
function resetClickToPlayCards(sectionEl) {
  sectionEl
    .querySelectorAll(
      '.testimonial-card__player-wrap[data-click-to-play="true"]'
    )
    .forEach(function (playerWrap) {
      if (!(playerWrap instanceof HTMLElement)) return;

      playerWrap.hidden = true;
      const card = playerWrap.closest('.testimonial-card--featured');
      card?.classList.remove('testimonial-card--playing');
    });
}

/**
 * @param {HTMLElement} sectionEl
 * @param {AbortSignal} signal
 */
function initFeaturedVideoPlay(sectionEl, signal) {
  sectionEl.addEventListener(
    'click',
    function (event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const playButton = target.closest('.testimonial-card__play');
      if (!(playButton instanceof HTMLButtonElement)) return;

      const card = playButton.closest('.testimonial-card--featured');
      if (!(card instanceof HTMLElement)) return;

      const playerWrap = card.querySelector('.testimonial-card__player-wrap');
      const playerEl = card.querySelector('[data-video-player]');
      if (!(playerWrap instanceof HTMLElement)) return;
      if (!(playerEl instanceof HTMLElement)) return;

      event.preventDefault();
      pauseAllVideos(sectionEl);
      playerWrap.hidden = false;
      card.classList.add('testimonial-card--playing');
      playerEl.dataset.autoplay = 'true';
      ensureVideoPlayer(playerEl);
      playVideoPlayer(playerEl);
      sectionEl.dispatchEvent(
        new CustomEvent('testimonials:featured-video-play', { bubbles: true })
      );
    },
    { signal }
  );
}

/**
 * @param {HTMLElement} sectionEl
 */
function initNonSwiperTestimonials(sectionEl) {
  const abortController = new AbortController();
  const { signal } = abortController;

  initVideoPlayers(sectionEl);
  initFeaturedVideoPlay(sectionEl, signal);

  function syncVisibleVideos() {
    syncResponsiveVideoPlayers(sectionEl);
    playAutoplayVideos(sectionEl);
  }

  const handleResize = debounce(function () {
    syncVisibleVideos();
  }, 150);

  window.addEventListener('resize', handleResize, { signal });

  const scroller = sectionEl.querySelector('.hs-scroll');
  if (scroller instanceof HTMLElement) {
    scroller.addEventListener('scroll', handleResize, {
      signal,
      passive: true,
    });
  }

  document.addEventListener(
    'visibilitychange',
    function () {
      if (document.hidden) {
        pauseAllVideos(sectionEl);
        resetClickToPlayCards(sectionEl);
      } else {
        syncVisibleVideos();
      }
    },
    { signal }
  );

  syncVisibleVideos();
}

/**
 * @param {ParentNode} [root]
 */
function bootNonSwiperTestimonials(root = document) {
  root.querySelectorAll('[data-testimonials]').forEach(function (sectionEl) {
    if (!(sectionEl instanceof HTMLElement)) return;
    if (sectionEl.dataset.autoscroll === 'true') return;
    initNonSwiperTestimonials(sectionEl);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    bootNonSwiperTestimonials();
  });
} else {
  bootNonSwiperTestimonials();
}

document.addEventListener('shopify:section:load', function (event) {
  const target = event.target;
  if (target instanceof Element) {
    bootNonSwiperTestimonials(target);
  }
});

registerSlider('[data-testimonials]', function (swiperEl, sectionEl) {
  if (sectionEl.dataset.autoscroll !== 'true') return null;

  const duration = Number(sectionEl.dataset.duration) || 4000;
  const shouldAutoplay =
    sectionEl.dataset.autoscroll === 'true' &&
    !shouldReduceMotion() &&
    !window.Shopify?.designMode;
  const reverseDirection =
    sectionEl.dataset.autoscrollDirection === 'right_to_left';
  const pauseOnHover = sectionEl.dataset.pauseOnHover !== 'false';
  const pauseOnFocus = sectionEl.dataset.pauseOnFocus !== 'false';
  const gapMobile = readGap(
    sectionEl,
    'gapMobile',
    readGap(sectionEl, 'gap', 15)
  );
  const gapTablet = readGap(sectionEl, 'gapTablet', gapMobile);
  const gapDesktop = readGap(sectionEl, 'gapDesktop', gapTablet);
  const columnsMobile = readSlidesPerView(
    sectionEl,
    'enableSwipeMobile',
    'visibleItemsMobile',
    'columnsMobile',
    1
  );
  const columnsTablet = readSlidesPerView(
    sectionEl,
    'enableSwipeTablet',
    'visibleItemsTablet',
    'columnsTablet',
    2
  );
  const columnsDesktop = readSlidesPerView(
    sectionEl,
    'enableSwipeDesktop',
    'visibleItemsDesktop',
    'columnsDesktop',
    3
  );
  const transitionDuration = shouldReduceMotion()
    ? REDUCED_MOTION_TRANSITION_DURATION
    : DEFAULT_TRANSITION_DURATION;

  let slides = getRealSlides(swiperEl);
  const hasMultipleSlides = slides.length > 1;

  /** @type {ReturnType<typeof setTimeout> | null} */
  let interactionResumeTimeout = null;
  /** @type {SwiperInstance | null} */
  let swiper = null;

  const abortController = new AbortController();
  const { signal } = abortController;

  initVideoPlayers(sectionEl);
  initFeaturedVideoPlay(sectionEl, signal);

  function syncVisibleVideos() {
    if (swiper) {
      pauseInactiveVideos(swiperEl, swiper.realIndex, swiper);
    }
    syncResponsiveVideoPlayers(sectionEl);
    playAutoplayVideos(sectionEl);
  }

  sectionEl.addEventListener(
    'testimonials:featured-video-play',
    function () {
      clearInteractionResume();
      pauseAutoplay();
    },
    { signal }
  );

  /**
   * @returns {SwiperAutoplay | null}
   */
  function getControllableAutoplay() {
    if (!swiper?.autoplay || !shouldAutoplay || slides.length <= 1) return null;
    return swiper.autoplay;
  }

  function canControlAutoplay() {
    return getControllableAutoplay() !== null;
  }

  function pauseAutoplay() {
    const autoplay = getControllableAutoplay();
    if (!autoplay) return;
    if (autoplay.running && !autoplay.paused) {
      autoplay.pause();
    }
  }

  function resumeAutoplay() {
    if (isFeaturedVideoPlaying(sectionEl)) return;

    const autoplay = getControllableAutoplay();
    if (!autoplay) return;
    if (pauseOnHover && sectionEl.matches(':hover')) return;
    if (pauseOnFocus && sectionEl.matches(':focus-within')) return;

    if (autoplay.paused && typeof autoplay.resume === 'function') {
      autoplay.resume();
      return;
    }

    if (!autoplay.running && typeof autoplay.start === 'function') {
      autoplay.start();
    }
  }

  function clearInteractionResume() {
    if (interactionResumeTimeout) {
      clearTimeout(interactionResumeTimeout);
      interactionResumeTimeout = null;
    }
  }

  function scheduleInteractionResume() {
    if (!canControlAutoplay()) return;
    clearInteractionResume();
    interactionResumeTimeout = setTimeout(function () {
      resumeAutoplay();
    }, INTERACTION_RESUME_DELAY);
  }

  function updateSlideAria() {
    slides = getRealSlides(swiperEl);
    const activeIndex = swiper?.realIndex ?? 0;
    const allSlidesVisible = slides.length <= 1 || swiper?.isLocked === true;
    const visibleIndexes = allSlidesVisible
      ? new Set(
          slides.map(function (_slide, index) {
            return index;
          })
        )
      : swiper
        ? getVisibleRealSlideIndexes(swiperEl, swiper)
        : new Set([activeIndex]);

    if (slides.length <= 1) {
      slides.forEach(function (slide) {
        slide.removeAttribute('role');
        slide.removeAttribute('aria-roledescription');
        slide.removeAttribute('aria-label');
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('inert');
      });
      return;
    }

    slides.forEach(function (slide, index) {
      const isVisible = visibleIndexes.has(index);
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute(
        'aria-label',
        formatSlideStatus(index + 1, slides.length)
      );

      if (isVisible) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }

      slide.toggleAttribute('inert', !isVisible);
    });
  }

  /**
   * @param {Event} event
   */
  var handleKeydown = function (event) {
    if (!swiper || slides.length <= 1) return;
    if (!(event instanceof KeyboardEvent)) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        swiper.slidePrev();
        break;
      case 'ArrowRight':
        event.preventDefault();
        swiper.slideNext();
        break;
      case 'Home':
        event.preventDefault();
        swiper.slideToLoop(0);
        break;
      case 'End':
        event.preventDefault();
        swiper.slideToLoop(slides.length - 1);
        break;
    }
  };

  var handleMouseEnter = function () {
    clearInteractionResume();
    pauseAutoplay();
  };

  var handleMouseLeave = function () {
    scheduleInteractionResume();
  };

  var handleFocusIn = function () {
    clearInteractionResume();
    pauseAutoplay();
  };

  /**
   * @param {Event} event
   */
  var handleFocusOut = function (event) {
    const focusEvent = /** @type {FocusEvent} */ (event);
    if (
      focusEvent.relatedTarget &&
      focusEvent.relatedTarget instanceof Node &&
      sectionEl.contains(focusEvent.relatedTarget)
    ) {
      return;
    }

    scheduleInteractionResume();
  };

  var handleVisibilityChange = function () {
    if (document.hidden) {
      pauseAutoplay();
      pauseAllVideos(sectionEl);
      resetClickToPlayCards(sectionEl);
    } else {
      resumeAutoplay();
      updateSlideAria();
      syncVisibleVideos();
    }
  };

  const handleResize = debounce(function () {
    if (swiper) {
      updateSwipeHint(sectionEl, swiper);
    }
    syncVisibleVideos();
  }, 150);

  sectionEl.addEventListener('keydown', handleKeydown, { signal });

  if (pauseOnHover) {
    sectionEl.addEventListener('mouseenter', handleMouseEnter, { signal });
    sectionEl.addEventListener('mouseleave', handleMouseLeave, { signal });
  }

  if (pauseOnFocus) {
    sectionEl.addEventListener('focusin', handleFocusIn, { signal });
    sectionEl.addEventListener('focusout', handleFocusOut, { signal });
  }

  document.addEventListener('visibilitychange', handleVisibilityChange, {
    signal,
  });

  window.addEventListener('resize', handleResize, { signal });

  return {
    slidesPerView: columnsMobile,
    spaceBetween: gapMobile,
    loop: hasMultipleSlides,
    speed: transitionDuration,
    watchOverflow: true,
    watchSlidesProgress: true,
    observer: true,
    observeParents: true,
    allowTouchMove: hasMultipleSlides,
    grabCursor: hasMultipleSlides,
    breakpoints: {
      640: {
        slidesPerView: columnsTablet,
        spaceBetween: gapTablet,
      },
      880: {
        slidesPerView: columnsDesktop,
        spaceBetween: gapDesktop,
      },
    },
    autoplay:
      shouldAutoplay && hasMultipleSlides
        ? {
            delay: duration,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
            reverseDirection: reverseDirection,
          }
        : false,
    a11y: { enabled: false },
    on: {
      /**
       * @param {SwiperInstance} sw
       */
      init: function (sw) {
        swiper = sw;
        updateSlideAria();
        updateSwipeHint(sectionEl, sw);
        syncVisibleVideos();
      },
      /**
       * @param {SwiperInstance} sw
       */
      lock: function (sw) {
        swiper = sw;
        updateSlideAria();
      },
      /**
       * @param {SwiperInstance} sw
       */
      unlock: function (sw) {
        swiper = sw;
        updateSlideAria();
      },
      /**
       * @param {SwiperInstance} sw
       */
      slideChange: function (sw) {
        swiper = sw;
        updateSlideAria();
        updateSwipeHint(sectionEl, sw);
        resetClickToPlayCards(sectionEl);
        scheduleInteractionResume();
      },
      /**
       * @param {SwiperInstance} sw
       */
      slideChangeTransitionEnd: function (sw) {
        swiper = sw;
        updateSlideAria();
        syncVisibleVideos();
      },
      /**
       * @param {SwiperInstance} sw
       */
      reachEnd: function (sw) {
        updateSwipeHint(sectionEl, sw);
      },
      /**
       * @param {SwiperInstance} sw
       */
      reachBeginning: function (sw) {
        updateSwipeHint(sectionEl, sw);
      },
      destroy: function () {
        abortController.abort();
        clearInteractionResume();
        pauseAllVideos(sectionEl);
        resetClickToPlayCards(sectionEl);
        swiper = null;
      },
    },
  };
});
